import { ReactNode, useMemo } from 'react';
import Image from 'next/image';

import { Box } from '@chakra-ui/react';

enum SponsorCardTheme {
  LIME,
  BLUE,
  GREEN,
  PURPLE,
}

function Shape({
  color,
  width,
  height,
  type,
}: {
  type: 'cube' | 'cylinder' | 'sphere';
  color: 'blue' | 'purple' | 'lime' | 'green';
  width: number;
  height: number;
}) {
  return (
    <Image
      alt=''
      height={height}
      sizes={`${width}px`}
      src={`/images/shapes/${type}-${color}.png`}
      width={width}
      style={{
        pointerEvents: 'none',
      }}
    />
  );
}
/**
 *
 * @param idx index number
 * @returns an array of StyleProps. The first element is the left shape, second element is the right shape, and the last element is either the top _or_ bottom shape.
 */
export function useFloatingShapes(idx: number): [ReactNode, ReactNode, ReactNode] {
  return useMemo(() => {
    switch (idx % 4) {
      default:
      case SponsorCardTheme.LIME: {
        const leftShape = (
          <Box
            key={`left-shape-${idx}`}
            left='-52px'
            position='absolute'
            top='100px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='lime' height={57} type='sphere' width={54} />
          </Box>
        );
        const rightShape = (
          <Box
            key={`right-shape-${idx}`}
            bottom='-40px'
            position='absolute'
            right='-52px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='lime' height={98} type='cylinder' width={93} />
          </Box>
        );
        const topShape = (
          <Box
            key={`top-shape-${idx}`}
            left='100px'
            position='absolute'
            top='-40px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='lime' height={80} type='cube' width={76} />
          </Box>
        );
        return [leftShape, rightShape, topShape];
      }
      case SponsorCardTheme.BLUE: {
        const leftShape = (
          <Box
            key={`left-shape-${idx}`}
            left='-56px'
            position='absolute'
            top='40px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='blue' height={80} type='cube' width={76} />
          </Box>
        );
        const rightShape = (
          <Box
            key={`right-shape-${idx}`}
            position='absolute'
            right='-55px'
            top='-45px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='blue' height={98} type='cylinder' width={93} />
          </Box>
        );
        const bottomShape = (
          <Box
            key={`bottom-shape-${idx}`}
            bottom='-40px'
            position='absolute'
            right='64px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='blue' height={46} type='sphere' width={44} />
          </Box>
        );
        return [leftShape, rightShape, bottomShape];
      }
      case SponsorCardTheme.GREEN: {
        const leftShape = (
          <Box
            key={`left-shape-${idx}`}
            left='-52px'
            position='absolute'
            top='65px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='green' height={57} type='sphere' width={57} />
          </Box>
        );
        const rightShape = (
          <Box
            key={`right-shape-${idx}`}
            position='absolute'
            right='-52px'
            top='-40px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='green' height={78} type='cube' width={80} />
          </Box>
        );
        const bottomShape = (
          <Box
            key={`bottom-shape-${idx}`}
            bottom='-45px'
            position='absolute'
            right='70px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='green' height={98} type='cylinder' width={98} />
          </Box>
        );
        return [leftShape, rightShape, bottomShape];
      }
      case SponsorCardTheme.PURPLE: {
        const leftShape = (
          <Box
            key={`left-shape-${idx}`}
            left='-52px'
            position='absolute'
            top='120px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='purple' height={91} type='cylinder' width={86} />
          </Box>
        );
        const rightShape = (
          <Box
            key={`right-shape-${idx}`}
            bottom='-40px'
            position='absolute'
            right='-40px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='purple' height={73} type='cube' width={93} />
          </Box>
        );
        const topShape = (
          <Box
            key={`top-shape-${idx}`}
            left='100px'
            position='absolute'
            top='-40px'
            transform={{
              base: 'scale(0.75)',
              md: 'scale(1)',
            }}
          >
            <Shape color='purple' height={48} type='sphere' width={46} />
          </Box>
        );
        return [leftShape, rightShape, topShape];
      }
    }
  }, [idx]);
}
